import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { formatDateTime } from "../utils/dateTime";
import AuditTabNav from "./AuditTabNav";

const SECTION_LABELS = {
  title: "Title Tag",
  description: "Meta Description",
  h1: "H1 Tag",
  canonical: "Canonical URL",
  metaRobots: "Robots Meta",
  images: "Image Alt Text",
  headingStructure: "Heading Structure",
  keywords: "Keyword Usage",
  https: "HTTPS",
  structuredData: "Structured Data",
  pageSpeed: "Page Speed",
  duplicates: "Duplicate Content",
};

const WEIGHTS = {
  title: 18,
  description: 13,
  h1: 15,
  canonical: 8,
  metaRobots: 5,
  images: 8,
  headingStructure: 8,
  keywords: 10,
  https: 5,
  structuredData: 7,
  pageSpeed: 5,
};

export function scoreColor(score) {
  if (score >= 80) return "var(--success)";
  if (score >= 60) return "#84cc16";
  if (score >= 40) return "var(--warning)";
  return "var(--danger)";
}

export function scoreLabel(score) {
  if (score >= 80) return "Excellent";
  if (score >= 60) return "Good";
  if (score >= 40) return "Needs Work";
  return "Poor";
}

export function ScoreRing({ score, size = 100 }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = scoreColor(score);

  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)" }}>
      <circle cx="50" cy="50" r={radius} fill="none" stroke="#e2e5ec" strokeWidth="8" />
      <circle
        cx="50" cy="50" r={radius}
        fill="none"
        stroke={color}
        strokeWidth="8"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.8s ease" }}
      />
      <text
        x="50" y="50"
        textAnchor="middle"
        dominantBaseline="middle"
        style={{ transform: "rotate(90deg)", transformOrigin: "50px 50px", fill: color, fontSize: "20px", fontWeight: 800, fontFamily: "inherit" }}
      >
        {score}
      </text>
    </svg>
  );
}

export function SeoScoreHero({ report, compact = false }) {
  const seoScore = report.seo_score ?? 0;
  const label = scoreLabel(seoScore);
  const color = scoreColor(seoScore);
  const heroSize = compact ? 110 : 170;
  const headingSize = compact ? "1.75rem" : "2.5rem";
  const scoreSize = compact ? "0.75rem" : "0.82rem";
  const showQuickStats = compact;

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", gap: compact ? 24 : 28, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative" }}>
          <ScoreRing score={seoScore} size={heroSize} />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: scoreSize, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", marginBottom: 4 }}>
            SEO Score
          </div>
          <div style={{ fontSize: headingSize, fontWeight: 800, color, letterSpacing: "-0.02em", marginBottom: 4 }}>
            {label}
          </div>
          <div style={{ fontSize: compact ? "0.85rem" : "0.95rem", color: "var(--text-muted)", maxWidth: compact ? 420 : 560 }}>
            {seoScore >= 80
              ? "Strong SEO fundamentals. Minor refinements could push scores even higher."
              : seoScore >= 60
              ? "Good baseline with clear opportunities. Address the issues below to improve rankings."
              : seoScore >= 40
              ? "Several important SEO elements need attention. Fix the critical issues first."
              : "Critical SEO problems detected. This page may be significantly underperforming in search."}
          </div>
        </div>

        {showQuickStats ? (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            {[
              { label: "Issues found", value: report.issues?.length ?? 0, alert: (report.issues?.length ?? 0) > 0 },
              { label: "Images missing alt", value: report.images?.missingAlt ?? 0, alert: (report.images?.missingAlt ?? 0) > 0 },
              { label: "H2 tags", value: Array.isArray(report.h2) ? report.h2.length : 0, alert: false },
            ].map(({ label, value, alert }) => (
              <div key={label} style={{ textAlign: "center", minWidth: 80 }}>
                <div style={{ fontSize: "1.5rem", fontWeight: 800, color: alert ? "var(--danger)" : "var(--text-heading)" }}>
                  {value}
                </div>
                <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BreakdownBar({ label, score, weight }) {
  const color = scoreColor(score);
  return (
    <div className="audit-breakdown-row">
      <div className="audit-breakdown-meta">
        <span className="audit-breakdown-label">{label}</span>
        <span className="audit-breakdown-score" style={{ color }}>
          {score}/100
          <span className="audit-breakdown-weight">({weight}% weight)</span>
        </span>
      </div>
      <div className="audit-breakdown-track">
        <div className="audit-breakdown-fill" style={{ width: `${score}%`, background: color }} />
      </div>
    </div>
  );
}

export default function SeoScoreCard({ report, showHero = true, showSavePromo = true }) {
  const [activeTab, setActiveTab] = useState("overview");
  const { isAuthenticated } = useAuth();

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "breakdown", label: "Score Breakdown" },
    { id: "issues", label: `Issues ${report.issues?.length ? `(${report.issues.length})` : ""}` },
    { id: "keywords", label: "Keywords" },
    { id: "details", label: "Page Details" },
  ];

  return (
    <div className="audit-score-card">
      {showHero ? <SeoScoreHero report={report} compact /> : null}

      <AuditTabNav tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab: Overview */}
      {activeTab === "overview" && (
        <div className="card">
          <div className="result-grid">
            {[
              { 
                label: (!report.title || report.title === "No title") ? "⚠️ Title" : "Title", 
                value: (!report.title || report.title === "No title") 
                  ? <span style={{ color: "var(--danger)", fontWeight: 600 }}>No title tag detected</span>
                  : report.title 
              },
              { 
                label: (!report.description || report.description === "No description") ? "⚠️ Meta Description" : "Meta Description", 
                value: (!report.description || report.description === "No description") 
                  ? <span style={{ color: "var(--warning)", fontWeight: 600 }}>No meta description detected</span>
                  : report.description 
              },
              { 
                label: (!report.h1 || report.h1 === "No H1") ? "⚠️ H1" : "H1", 
                value: (!report.h1 || report.h1 === "No H1") 
                  ? <span style={{ color: "var(--danger)", fontWeight: 600 }}>No H1 detected</span>
                  : report.h1 
              },
              { 
                label: "Canonical URL", 
                value: (!report.canonical_url || report.canonical_url === "Not set") 
                  ? <span style={{ color: "var(--warning)" }}>Not set</span> 
                  : report.canonical_url 
              },
              { 
                label: "Robots Meta", 
                value: (!report.meta_robots || report.meta_robots === "Not set") 
                  ? "Not set (defaults to index, follow)" 
                  : report.meta_robots 
              },
              {
                label: "Images",
                value: (report.images?.missingAlt > 0)
                  ? <span style={{ color: "var(--warning)", fontWeight: 600 }}>{`${report.images.total ?? 0} total · ${report.images.missingAlt} missing alt`}</span>
                  : <span style={{ color: "var(--success)" }}>{`${report.images?.total ?? 0} total · 0 missing alt ✓`}</span>
              },
            ].map(({ label, value }, idx) => (
              <div className="result-item" key={idx}>
                <div className="result-label">{label}</div>
                <div className="result-value">{value || "—"}</div>
              </div>
            ))}
          </div>

          {report.h2?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div className="result-label" style={{ marginBottom: 8 }}>H2 Tags ({report.h2.length})</div>
              <ul className="tag-list">
                {report.h2.map((h2, i) => (
                  <li className="tag" key={i}>{h2}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Tab: Score Breakdown */}
      {activeTab === "breakdown" && (
        <div className="card audit-panel-card">
          <p className="audit-panel-lead">
            Each factor is scored 0–100 and weighted by SEO impact. Weighted total gives the overall score.
          </p>
          {report.scoreSections
            ? Object.entries(report.scoreSections).map(([key, section]) => (
                <BreakdownBar
                  key={key}
                  label={SECTION_LABELS[key] || key}
                  score={section.score}
                  weight={WEIGHTS[key] || 0}
                />
              ))
            : <p style={{ color: "var(--text-muted)" }}>Score breakdown not available for this audit.</p>}

          <div style={{ marginTop: 24, padding: 16, background: "#f8fafc", borderRadius: 8, border: "1px solid var(--border)", fontSize: "0.875rem" }}>
            <div style={{ fontWeight: 600, color: "var(--text-heading)", marginBottom: 8 }}>
              Overall Score Calculation Formula:
            </div>
            <div style={{ fontFamily: "ui-monospace, monospace", color: "var(--text-muted)", lineHeight: 1.6, marginBottom: 8 }}>
              Overall Score = Math.round( Σ (Section Score × Weight) / Σ (Weights) )
            </div>
            <div style={{ color: "var(--text)", lineHeight: 1.5 }}>
              Current Math: <strong>{report.seo_score}</strong> = Math.round(
              {" "}{Object.entries(report.scoreSections || {})
                .map(([key, section]) => `${section.score} × ${WEIGHTS[key] || 0}%`)
                .join(" + ")}
              {" "}/ {Object.keys(report.scoreSections || {}).reduce((acc, key) => acc + (WEIGHTS[key] || 0), 0)}%
              )
              {(!report.h1 || report.h1 === "No H1" || report.h1Count === 0) && (
                <span style={{ display: "block", color: "var(--danger)", marginTop: 8, fontWeight: 600 }}>
                  ⚠️ Note: Since this page is missing an H1 tag, a hard cap of 60 is applied to the final score.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Issues */}
      {activeTab === "issues" && (
        <div className="card">
          {!report.issues?.length ? (
            <div style={{ color: "var(--success)", fontWeight: 600, padding: "12px 0" }}>
              ✓ No SEO issues found on this page. Excellent work!
            </div>
          ) : (
            <>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: 16 }}>
                Issues are ordered by priority. Fix critical items (score 0–30) first.
              </p>
              {report.issues.map((item, i) => {
                const isCritical = item.score < 30;
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: "12px 14px",
                      marginBottom: 8,
                      borderRadius: "var(--radius-sm)",
                      borderLeft: `4px solid ${isCritical ? "var(--danger)" : "var(--warning)"}`,
                      background: isCritical ? "#fff5f5" : "#fffbeb",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        background: "#e2e8f0",
                        color: "#475569",
                        padding: "2px 8px",
                        borderRadius: 10,
                        whiteSpace: "nowrap",
                        marginTop: 2,
                      }}
                    >
                      {SECTION_LABELS[item.category] || item.category}
                    </span>
                    <span style={{ fontSize: "0.875rem", color: "var(--text)" }}>{item.issue}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Tab: Keywords */}
      {activeTab === "keywords" && (
        <div className="card">
          {!report.topKeywords?.length ? (
            <p style={{ color: "var(--text-muted)" }}>No keyword data available for this audit.</p>
          ) : (
            <>
              <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginBottom: 16 }}>
                Top keywords by frequency. Check that your target keyword appears in the title, description, and H1.
              </p>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Keyword</th>
                      <th>Count</th>
                      <th>Density</th>
                      <th>In Title</th>
                      <th>In Description</th>
                      <th>In H1</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.topKeywords.map((kw, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{kw.word}</td>
                        <td>{kw.count}</td>
                        <td>
                          <span
                            style={{
                              color: kw.density > 5 ? "var(--danger)" : kw.density > 3 ? "var(--warning)" : "var(--success)",
                              fontWeight: 600,
                            }}
                          >
                            {kw.density}%
                          </span>
                        </td>
                        <td>{kw.inTitle ? "✓" : <span style={{ color: "var(--danger)" }}>✗</span>}</td>
                        <td>{kw.inDescription ? "✓" : <span style={{ color: "var(--warning)" }}>✗</span>}</td>
                        <td>{kw.inH1 ? "✓" : <span style={{ color: "var(--danger)" }}>✗</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Tab: Page Details */}
      {activeTab === "details" && (
        <div className="card audit-panel-card">
          <div className="audit-detail-list">
            {[
              ["URL", report.url],
              ["Title", `${report.title || "—"} ${report.title && report.title !== "No title" ? `(${report.title.length} chars)` : ""}`],
              ["Description", `${report.description || "—"} ${report.description && report.description !== "No description" ? `(${report.description.length} chars)` : ""}`],
              ["H1", report.h1],
              ["H1 Count", report.h1Count ?? 1],
              ["H2 Count", Array.isArray(report.h2) ? report.h2.length : 0],
              ["Canonical", report.canonical_url],
              ["Robots", (!report.meta_robots || report.meta_robots === "Not set") ? "Not set (defaults to index, follow)" : report.meta_robots],
              ["Total Images", report.images?.total ?? 0],
              ["Images Missing Alt", report.images?.missingAlt ?? 0],
              ["Audited at", report.created_at ? formatDateTime(report.created_at) : "—"],
            ].map(([key, value]) => (
              <div key={key} className="audit-detail-row">
                <span className="audit-detail-key">{key}</span>
                <span className="audit-detail-value">{value ?? "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isAuthenticated && showSavePromo && (
        <div className="card animate-fade-in" style={{
          marginTop: 32,
          background: "linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(29, 78, 216, 0.05) 100%)",
          border: "1px solid rgba(37, 99, 235, 0.2)",
          padding: "32px 24px",
          textAlign: "center",
          borderRadius: 12,
          boxShadow: "0 4px 20px -2px rgba(37, 99, 235, 0.05)"
        }}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "1.35rem", color: "var(--text-heading)", fontWeight: 800 }}>
            Save this report & monitor changes
          </h3>
          <p style={{ margin: "0 0 24px 0", color: "var(--text-muted)", fontSize: "0.95rem", lineHeight: 1.5 }}>
            Get alerts when your score drops, track historical performance, and unlock deep audits.
          </p>
          <a
            href="/login"
            className="btn btn-primary"
            style={{ padding: "10px 28px", borderRadius: 8, fontSize: "0.95rem", display: "inline-block", fontWeight: 600 }}
          >
            Create Free Account
          </a>
        </div>
      )}
    </div>
  );
}
